import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import FilterErrorIllustration from '@hotjar/shared-assets/images/error-capturing.png';
import TrendsSplashIllustration from '@hotjar/shared-assets/images/trends-splash.svg';
import FunnelsSplashIllustration from '@hotjar/shared-assets/images/funnels-splash.svg';

import { ContextId, ModalId } from './enums';
import type { Context } from './types';
import CustomReactionScalesIllustration from '../assets/images/custom-reaction-scales.png';
import FiltersIllustration from '../assets/images/filters.svg';
import FilterNpsIllustration from '../assets/images/filter-nps.svg';
import FilterUTurnIllustration from '../assets/images/filter-u-turn.svg';
import ObserveGoogleOptimizeIllustration from '../assets/images/google-optimize.png';
import IncomingFeedbackIllustration from '../assets/images/incoming-feedback.svg';
import ObserveOptimizelyIllustration from '../assets/images/optimizely.png';
import PageTargetingIllustration from '../assets/images/page-targeting.svg';
import PercentageTargetingIllustration from '../assets/images/percentage-targeting.svg';
import RemoveBrandingIllustration from '../assets/images/remove-branding.png';
import ObserveSlackIllustration from '../assets/images/slack.png';
import ObserveMsTeamsIllustration from '../assets/images/ms-teams.png';
import SurveyCustomizationIllustration from '../assets/images/survey-customization.png';
import SurveysIllustration from '../assets/images/surveys.svg';
import ObserveZapierIllustration from '../assets/images/zapier.png';
import SurveyQuestionImagesIllustration from '../assets/images/survey-question-images.svg';
import PublicApiIllustration from '../assets/images/responses-api-image.svg';

/**
 * List of upsell contexts. An upsell context can control how an upsell modal behaves, what product features it
 * emphasizes, and what plans are allowed to be selected in the given upsell scenario.
 */
export const contexts = [
  {
    id: ContextId.Default,
    modalId: ModalId.MultiProduct,
    product: null,
  },
  {
    id: ContextId.Traffic,
    modalId: ModalId.MultiProductPlanSelect,
    product: ProductType.Analytics,
  },
  {
    id: ContextId.CustomReactionScales,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.custom-reaction-scales.title',
        description: 'feature-gates.custom-reaction-scales.description',
        illustration: CustomReactionScalesIllustration,
      },
    },
    // featureName: 'feedback.emotion_style_choices',
    product: ProductType.Feedback,
  },
  {
    id: ContextId.ObserveEventsApi,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.observe-events-api.title',
        description: 'feature-gates.observe-events-api.description',
        illustration: FiltersIllustration,
      },
    },
    // minimumFeatureGroup: PLUS,
    product: ProductType.Analytics,
  },
  {
    id: ContextId.AskEventsApi,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.ask-events-api.title',
        description: 'feature-gates.ask-events-api.description',
        illustration: FiltersIllustration,
      },
    },
    // minimumFeatureGroup: PLUS,
    product: ProductType.Feedback,
  },
  {
    id: ContextId.FilterIncomingFeedback,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.filter-incoming-feedback.title',
        description: 'feature-gates.filter-incoming-feedback.description',
        illustration: IncomingFeedbackIllustration,
      },
    },
    // featureName: 'filters.incoming_feedback',
    product: ProductType.Analytics,
  },
  {
    id: ContextId.FilterNps,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.filter-nps.title',
        description: 'feature-gates.filter-nps.description',
        illustration: FilterNpsIllustration,
      },
    },
    // featureName: 'filters.nps',
    product: ProductType.Analytics,
  },
  {
    id: ContextId.FilterRageClicks,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.filter-rage-clicks.title',
        description: 'feature-gates.filter-rage-clicks.description',
        illustration: PageTargetingIllustration,
      },
    },
    // featureName: 'filters.rage_click',
    product: ProductType.Analytics,
  },
  {
    id: ContextId.FilterReferrerUrl,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.filter-referrer-url.title',
        description: 'feature-gates.filter-referrer-url.description',
        illustration: FiltersIllustration,
      },
    },
    // featureName: 'filters.referrer_url',
    product: ProductType.Analytics,
  },
  {
    id: ContextId.FilterUserAttributes,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.filter-user-attributes.title',
        description: 'feature-gates.filter-user-attributes.description',
        illustration: FiltersIllustration,
      },
    },
    // featureName: 'filters.user_attribute',
    product: ProductType.Analytics,
  },
  {
    id: ContextId.FilterUTurn,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.filter-u-turn.title',
        description: 'feature-gates.filter-u-turn.description',
        illustration: FilterUTurnIllustration,
      },
    },
    // featureName: 'filters.u_turn',
    product: ProductType.Analytics,
  },
  {
    id: ContextId.FilterClicked,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.filter-clicked.title',
        description: 'feature-gates.filter-clicked.description',
        illustration: FiltersIllustration,
      },
    },
    // featureName: 'filters.clicked',
    // minimumFeatureGroup: BUSINESS,
    product: ProductType.Analytics,
  },
  {
    id: ContextId.FilterError,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.filter-error.title',
        description: 'feature-gates.filter-error.description',
        illustration: FilterErrorIllustration,
      },
    },
    // minimumFeatureGroup: SCALE,
    product: ProductType.Analytics,
  },

  {
    id: ContextId.IncomingFeedback,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.incoming-feedback.title',
        description: 'feature-gates.incoming-feedback.description',
        illustration: IncomingFeedbackIllustration,
      },
    },
    // minimumFeatureGroup: PLUS,
    product: ProductType.Feedback,
  },
  {
    id: ContextId.PageTargeting,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.page-targeting.title',
        description: 'feature-gates.page-targeting.description',
        illustration: PageTargetingIllustration,
      },
    },
    // featureName: 'playbacks.page_targeting',
    product: ProductType.Analytics,
  },
  {
    id: ContextId.PercentageTargeting,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.percentage-targeting.title',
        description: 'feature-gates.percentage-targeting.description',
        illustration: PercentageTargetingIllustration,
      },
    },
    // featureName: 'targeting.cap',
    product: ProductType.Feedback,
  },
  {
    id: ContextId.RemoveHotjarBranding,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.remove-hotjar-branding.title',
        description: 'feature-gates.remove-hotjar-branding.description',
        illustration: RemoveBrandingIllustration,
      },
    },
    // featureName: 'branding.toggle',
    product: ProductType.Feedback,
  },
  {
    id: ContextId.SurveyCustomization,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.survey-customization.title',
        description: 'feature-gates.survey-customization.description',
        illustration: SurveyCustomizationIllustration,
      },
    },
    // featureName: 'survey.appearance.colors',
    product: ProductType.Feedback,
  },
  {
    id: ContextId.SurveyPerformance,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.survey-performance.title',
        description: 'feature-gates.survey-performance.description',
        illustration: SurveysIllustration,
      },
    },
    // minimumFeatureGroup: BUSINESS,
    product: ProductType.Feedback,
  },
  {
    id: ContextId.Surveys,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.surveys.title',
        description: 'feature-gates.surveys.description',
        illustration: SurveysIllustration,
      },
    },
    // minimumFeatureGroup: PLUS,
    product: ProductType.Feedback,
  },
  {
    id: ContextId.UserAttributes,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.user-attributes.title',
        description: 'feature-gates.user-attributes.description',
        illustration: FiltersIllustration,
      },
    },
    // featureName: 'user_attributes.toggle',
    product: ProductType.Feedback,
  },
  {
    id: ContextId.AskAdvancedFilters,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.ask-advanced-filters.title',
        description: 'feature-gates.ask-advanced-filters.description',
        illustration: FiltersIllustration,
      },
    },
    // minimumFeatureGroup: PLUS,
    product: ProductType.Feedback,
  },
  {
    id: ContextId.ObserveAdvancedFilters,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.observe-advanced-filters.title',
        description: 'feature-gates.observe-advanced-filters.description',
        illustration: FiltersIllustration,
      },
    },
    // minimumFeatureGroup: PLUS,
    product: ProductType.Analytics,
  },
  {
    id: ContextId.ObserveBusinessFilters,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.observe-business-filters.title',
        description: 'feature-gates.observe-business-filters.description',
        illustration: FiltersIllustration,
      },
    },
    // minimumFeatureGroup: BUSINESS,
    product: ProductType.Analytics,
  },
  {
    id: ContextId.ObserveSlack,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.observe-slack.title',
        description: 'feature-gates.observe-slack.description',
        illustration: ObserveSlackIllustration,
      },
    },
    // featureName: 'settings.integrations_slack',
    product: ProductType.Analytics,
  },
  {
    id: ContextId.ObserveMsTeams,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.ask-ms-teams.title',
        description: 'feature-gates.ask-ms-teams.description',
        illustration: ObserveMsTeamsIllustration,
      },
    },
    // featureName: 'settings.integrations_teams',
    // minimumFeatureGroup: BUSINESS,
    product: ProductType.Analytics,
  },
  {
    id: ContextId.Optimizely,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.optimizely.title',
        description: 'feature-gates.optimizely.description',
        illustration: ObserveOptimizelyIllustration,
      },
    },
    // featureName: 'settings.integrations_optimizely',
    product: ProductType.Analytics,
  },
  {
    id: ContextId.ObserveZapier,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.observe-zapier.title',
        description: 'feature-gates.observe-zapier.description',
        illustration: ObserveZapierIllustration,
      },
    },
    // featureName: 'settings.integrations_zapier',
    product: ProductType.Analytics,
  },
  {
    id: ContextId.AskZapier,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.ask-zapier.title',
        description: 'feature-gates.ask-zapier.description',
        illustration: ObserveZapierIllustration,
      },
    },
    // featureName: 'settings.integrations_zapier',
    product: ProductType.Feedback,
  },
  {
    id: ContextId.Trends,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.trends.title',
        description: 'feature-gates.trends.description',
        illustration: TrendsSplashIllustration,
      },
    },
    // minimumFeatureGroup: SCALE,
    product: ProductType.Analytics,
  },
  {
    id: ContextId.Funnels,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.funnels.title',
        description: 'feature-gates.funnels.description',
        illustration: FunnelsSplashIllustration,
      },
    },
    // minimumFeatureGroup: SCALE,
    product: ProductType.Analytics,
  },
  {
    id: ContextId.GoogleOptimize,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.google-optimize.title',
        description: 'feature-gates.google-optimize.description',
        illustration: ObserveGoogleOptimizeIllustration,
      },
    },
    // featureName: 'settings.integrations_google_optimize',
    product: ProductType.Analytics,
  },
  {
    id: ContextId.AskSlack,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.ask-slack.title',
        description: 'feature-gates.ask-slack.description',
        illustration: ObserveSlackIllustration,
      },
    },
    // featureName: 'settings.integrations_slack',
    product: ProductType.Feedback,
  },
  {
    id: ContextId.AskMsTeams,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.ask-ms-teams.title',
        description: 'feature-gates.ask-ms-teams.description',
        illustration: ObserveMsTeamsIllustration,
      },
    },
    // featureName: 'settings.integrations_teams',
    // minimumFeatureGroup: BUSINESS,
    product: ProductType.Feedback,
  },
  {
    id: ContextId.FilterUserSegmentsPlus,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.user-segments.title',
        description: 'feature-gates.user-segments.description',
        illustration: PageTargetingIllustration,
      },
    },
    product: ProductType.Analytics,
    // minimumFeatureGroup: PLUS,
  },
  {
    id: ContextId.FilterUserSegmentsBusiness,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.user-segments.title',
        description: 'feature-gates.user-segments.description',
        illustration: PageTargetingIllustration,
      },
    },
    product: ProductType.Analytics,
    // minimumFeatureGroup: BUSINESS,
  },
  {
    id: ContextId.FilterUserSegmentsScale,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.user-segments.title',
        description: 'feature-gates.user-segments.description',
        illustration: PageTargetingIllustration,
      },
    },
    product: ProductType.Analytics,
    // minimumFeatureGroup: SCALE,
  },
  {
    id: ContextId.AskResponseLimit,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      header: {
        title: 'title-ask-response-limit',
      },
      contextualBar: {
        title: 'feature-gates.ask-response-limit.title',
        description: 'feature-gates.ask-response-limit.description',
        illustration: IncomingFeedbackIllustration,
      },
    },
    product: ProductType.Feedback,
    // isPlanAgnostic: true,
  },
  {
    id: ContextId.SurveyQuestionImages,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.survey-question-images.title',
        description: 'feature-gates.survey-question-images.description',
        illustration: SurveyQuestionImagesIllustration,
      },
    },
    product: ProductType.Feedback,
    // minimumFeatureGroup: BUSINESS,
  },
  {
    id: ContextId.PublicAPI,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.public-api.title',
        description: 'feature-gates.public-api.description',
        illustration: PublicApiIllustration,
      },
    },
    product: ProductType.Feedback,
    // minimumFeatureGroup: SCALE,
  },
  {
    id: ContextId.ErrorConsole,
    modalId: ModalId.MultiProductPlanSelect,
    modalProps: {
      contextualBar: {
        title: 'feature-gates.error-console.title',
        description: 'feature-gates.error-console.description',
        illustration: FilterErrorIllustration,
      },
    },
    product: ProductType.Analytics,
    // minimumFeatureGroup: SCALE,
  },
] as const satisfies ReadonlyArray<Context>;
